import { useCallback, useEffect, useRef, useState } from 'react';
import { playMicStart, playMicStop } from './sounds';

/**
 * Centralized mic state for the role-play and ask-coach surfaces.
 *
 * The CodeBaby loader owns the actual mic stream — we drive it by calling the
 * session's `microphoneToggle()`. This hook layers on:
 *   - explicit states (idle / listening / committing / error) so the UI can
 *     render distinct treatments instead of a single boolean,
 *   - a permission probe so we know whether tapping the mic will trigger the
 *     browser prompt and can show our own explainer first,
 *   - error recovery for denial / unsupported environments.
 *
 * The hook is presentation-aware but has no DOM. Host screens render the
 * pre-prompt and error overlays based on `showingPrePrompt` / `errorKind`.
 */

export type MicState = 'idle' | 'listening' | 'committing' | 'error';

export type MicErrorKind = 'denied' | 'unavailable' | 'unsupported' | 'unknown';

export interface MicControllerOptions {
  /** Calls the underlying CodeBaby session's microphoneToggle. */
  toggle: () => void;
  /** True once the avatar session is ready to take mic input. */
  sessionReady: boolean;
  /** When set, controller stays idle and ignores taps. */
  disabled?: boolean;
  /**
   * Conversation mode: the loader runs a continuous (diarized) listener that
   * stays open across turns. In this mode the mic button is a status
   * indicator, not a per-turn toggle, so taps are ignored — and the open mic
   * would otherwise capture our own start/stop chimes as speech, so we
   * suppress them. See CODEBABY_GUIDE.md §4.
   */
  continuous?: boolean;
}

export interface MicController {
  state: MicState;
  errorKind: MicErrorKind | null;
  permission: PermissionState | 'unsupported';
  showingPrePrompt: boolean;
  /** User tapped the mic button — host calls this. */
  onTap: () => void;
  /** Host calls when CodeBaby emits an interim mic-data event. */
  onInterim: () => void;
  /** Host calls when CodeBaby emits a final mic-data event. */
  onFinal: (hadText: boolean) => void;
  /**
   * Host calls when the LOADER reports its mic is now actively listening
   * (`na-microphoneReady`). Drives the visual to "listening" without the
   * user having tapped — covers the loader's auto-reopen behavior after
   * each persona response.
   */
  onSessionMicListening: () => void;
  /**
   * Host calls when the LOADER reports its mic has closed
   * (`na-microphoneStopped`). Drops state to idle unless we're already
   * 'committing' (in which case onFinal is about to handle the cycle).
   */
  onSessionMicIdle: () => void;
  /** User confirmed the pre-prompt — start listening. */
  confirmPrePrompt: () => void;
  /** User dismissed the pre-prompt without starting. */
  cancelPrePrompt: () => void;
  /** Try again after an error. */
  retry: () => void;
  /** Hide the error UI without retrying. */
  dismissError: () => void;
}

const PRE_PROMPT_KEY = 'sa-mic-pre-prompted';

async function queryPermission(): Promise<PermissionState | 'unsupported'> {
  try {
    if (!navigator.permissions?.query) return 'unsupported';
    const status = await navigator.permissions.query({
      name: 'microphone' as PermissionName,
    });
    return status.state;
  } catch {
    return 'unsupported';
  }
}

function readPrePrompted(): boolean {
  try {
    return localStorage.getItem(PRE_PROMPT_KEY) === '1';
  } catch {
    return false;
  }
}

function markPrePrompted() {
  try {
    localStorage.setItem(PRE_PROMPT_KEY, '1');
  } catch {
    /* private mode — fine, just shows again next time */
  }
}

export function useMicController({
  toggle,
  sessionReady,
  disabled = false,
  continuous = false,
}: MicControllerOptions): MicController {
  const [state, setState] = useState<MicState>('idle');
  const [errorKind, setErrorKind] = useState<MicErrorKind | null>(null);
  const [permission, setPermission] = useState<PermissionState | 'unsupported'>('prompt');
  const [showingPrePrompt, setShowingPrePrompt] = useState(false);

  const stateRef = useRef(state);
  stateRef.current = state;

  // Probe permission on mount; subscribe to permission changes so a manual
  // grant in browser settings flips us out of the error state automatically.
  useEffect(() => {
    let cancelled = false;
    let status: PermissionStatus | null = null;
    (async () => {
      const initial = await queryPermission();
      if (cancelled) return;
      setPermission(initial);
      if (initial === 'unsupported') return;
      try {
        status = await navigator.permissions.query({
          name: 'microphone' as PermissionName,
        });
        const handler = () => {
          if (cancelled || !status) return;
          setPermission(status.state);
        };
        status.onchange = handler;
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
      if (status) status.onchange = null;
    };
  }, []);

  // Permission flipped to denied while we're listening → force-stop and
  // show the recovery UI. The toggle call hands the mic back to CodeBaby.
  useEffect(() => {
    if (
      permission === 'denied' &&
      (stateRef.current === 'listening' || stateRef.current === 'committing')
    ) {
      toggle();
      setState('error');
      setErrorKind('denied');
    }
  }, [permission, toggle]);

  const beginListening = useCallback(() => {
    if (disabled || !sessionReady) return;
    setErrorKind(null);
    toggle();
    setState('listening');
  }, [disabled, sessionReady, toggle]);

  const stopListening = useCallback(() => {
    toggle();
    setState('idle');
  }, [toggle]);

  const onTap = useCallback(() => {
    if (disabled) return;
    // Conversation mode: the listener is always-on and the button is just a
    // live indicator. Tapping shouldn't toggle the loader off mid-conversation.
    if (continuous) return;
    if (state === 'listening') {
      stopListening();
      return;
    }
    if (state === 'committing') return;
    if (permission === 'denied') {
      setState('error');
      setErrorKind('denied');
      return;
    }
    // First tap in this browser profile — show our explainer before the
    // browser's own permission prompt fires. Skip when permission is already
    // granted (no prompt will fire) or when we've shown the explainer once.
    if (permission !== 'granted' && !readPrePrompted()) {
      setShowingPrePrompt(true);
      return;
    }
    beginListening();
  }, [disabled, continuous, state, permission, beginListening, stopListening]);

  const confirmPrePrompt = useCallback(() => {
    setShowingPrePrompt(false);
    markPrePrompted();
    beginListening();
  }, [beginListening]);

  const cancelPrePrompt = useCallback(() => {
    setShowingPrePrompt(false);
  }, []);

  const onInterim = useCallback(() => {
    // No-op for now — placeholder for V2's silence-detection countdown.
    // Kept in the interface so host screens wire it up once and don't need
    // a code change when V2 lands.
  }, []);

  const onFinal = useCallback((hadText: boolean) => {
    if (!hadText) {
      // CodeBaby fires an empty final on toggle-off — treat as a clean stop,
      // not a commit.
      setState('idle');
      return;
    }
    setState('committing');
    // Host submits during this window. Drop to idle on the next tick.
    setTimeout(() => {
      if (stateRef.current === 'committing') setState('idle');
    }, 150);
  }, []);

  const retry = useCallback(() => {
    setErrorKind(null);
    setState('idle');
    // Re-trigger the tap flow. If permission is still denied, the user
    // will land back in the recovery UI immediately.
    if (permission === 'denied') {
      setState('error');
      setErrorKind('denied');
      return;
    }
    if (permission !== 'granted' && !readPrePrompted()) {
      setShowingPrePrompt(true);
      return;
    }
    beginListening();
  }, [permission, beginListening]);

  const dismissError = useCallback(() => {
    setErrorKind(null);
    setState('idle');
  }, []);

  const onSessionMicListening = useCallback(() => {
    // The loader signals its mic is now capturing. Mirror that in our state
    // so the button visual matches reality. If we're 'committing' we're in
    // the brief window where the host is submitting a final transcript —
    // ignore the loader's mic-ready in that window and let onFinal flow
    // naturally.
    if (stateRef.current === 'committing' || stateRef.current === 'error') return;
    // Audible cue — only on actual listening transitions, not duplicate
    // events. The chime is what makes "the mic just opened" obvious for
    // mobile / headphones-in use. Suppressed in conversation mode (the
    // always-open mic would hear the chime and transcribe it as speech).
    if (stateRef.current !== 'listening') {
      if (!continuous) playMicStart();
      setState('listening');
    }
  }, [continuous]);

  const onSessionMicIdle = useCallback(() => {
    // Loader closed the mic. If we're 'committing', the host is in the
    // middle of submitting; let onFinal handle the transition. Otherwise
    // drop to idle.
    if (stateRef.current === 'committing' || stateRef.current === 'error') return;
    if (stateRef.current === 'listening' && !continuous) {
      playMicStop();
    }
    if (stateRef.current !== 'idle') setState('idle');
  }, [continuous]);

  return {
    state,
    errorKind,
    permission,
    showingPrePrompt,
    onTap,
    onInterim,
    onFinal,
    onSessionMicListening,
    onSessionMicIdle,
    confirmPrePrompt,
    cancelPrePrompt,
    retry,
    dismissError,
  };
}
